import type { EstimateInsuranceOption } from '@massilia/queries'

import { withFieldGroup } from '@massilia/features'
import { cn } from '@massilia/ui'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import {
  Calendar,
  ExternalLink,
  FileCheck,
  Hash,
  Info,
  Newspaper,
  Shield,
  Sparkles,
} from 'lucide-react'

import type { ReservationSchema } from '@/lib/schemas/reservation/reservation.schema'

import {
  getSeatInformationLabel,
  getSeatInformationOptions,
  getSeatInformationText,
  type SeatOption,
} from '@/components/reservation/utils'
import { ImportantNotice } from '@/components/ui/important-notice'

import { SectionWrapper } from '../-components/section-wrapper'
import { BOOLEAN_OPTIONS, NEWSLETTER_OPTIONS } from '../-utils/constants'

interface FinalOptionsFormGroupProps {
  companyIataCode: string
  estimateDate: Date
  estimateReference: string
  estimateType: 'flight' | 'tourism'
  /**
   * Un envoi refusé par la validation. Le bouton ne partait alors sans rien
   * dire : ni requête, ni message. Un clic qui ne fait rien est le pire des
   * retours — le client recommence, et finit par appeler l'agence.
   */
  hasValidationErrors: boolean
  insuranceOption?: EstimateInsuranceOption | null
  isLongTrip: boolean
  isSubmitting: boolean
  totalPrice: number // in cents
  totalPriceWithInsurance: number // in cents
}

export const FinalOptionsFormGroup = withFieldGroup({
  defaultValues: {} as Pick<
    ReservationSchema,
    | 'acceptsTermsAndConditions'
    | 'animalInformation'
    | 'newsletterSubscription'
    | 'seatInformation'
    | 'wantsCarLoan'
    | 'wantsInsurance'
    | 'wantsInvoice'
  >,
  props: {} as FinalOptionsFormGroupProps,
  render: function FinalOptions({
    companyIataCode,
    estimateDate,
    estimateReference,
    estimateType,
    group,
    hasValidationErrors,
    insuranceOption,
    isLongTrip,
    isSubmitting,
    totalPrice,
    totalPriceWithInsurance,
  }) {
    const seatOptions = getSeatInformationOptions(companyIataCode).map((option: SeatOption) => ({
      label: option.label,
      value: option.label,
    }))
    const seatInfoText = getSeatInformationText(companyIataCode) ?? ''
    const seatInfoLabel = getSeatInformationLabel(companyIataCode)

    const hasSeatOptions = seatOptions.length > 0
    const showCarLoan = estimateType !== 'tourism'

    return (
      <SectionWrapper
        icon={FileCheck}
        title="Finalisation"
        description="Options de voyage et confirmation de votre réservation"
        variant="highlight"
      >
        <div className="space-y-6">
          {/* Seat options */}
          {hasSeatOptions && (
            <div className="space-y-4">
              <group.AppField
                name="seatInformation"
                children={(field) => (
                  <field.RadioGroupField
                    label={seatInfoLabel ?? 'Préférence de siège'}
                    options={seatOptions}
                    orientation="horizontal"
                    optional
                  />
                )}
              />
              {seatInfoText && (
                <div className="flex gap-3 rounded-lg border border-amber-200/60 bg-amber-50/50 p-3">
                  <Info className="h-4 w-4 shrink-0 text-amber-600" />
                  <p className="text-xs leading-relaxed whitespace-pre-line text-amber-800">
                    {seatInfoText}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Animal information */}
          <group.AppField
            name="animalInformation"
            children={(field) => (
              <field.TextAreaField
                label="Voyagez-vous avec un animal ?"
                placeholder="Race, poids, dimensions de la cage..."
                optional
              />
            )}
          />

          {/* Car rental option (flight only) */}
          {showCarLoan && (
            <group.AppField
              name="wantsCarLoan"
              children={(field) => (
                <field.RadioGroupField
                  label="Acceptez-vous de recevoir un devis pour une location de voiture ?"
                  options={BOOLEAN_OPTIONS}
                  orientation="horizontal"
                />
              )}
            />
          )}

          {/* Divider */}
          <div className="border-t border-amber-200/60" />

          {/* Estimate info row (read-only display) */}
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1">
              <span className="text-xs font-medium tracking-wider text-amber-700 uppercase">
                Date du devis
              </span>
              <p className="flex items-center gap-2 text-sm font-medium text-stone-800">
                <Calendar className="h-4 w-4 text-amber-600" />
                {format(estimateDate, 'dd MMMM yyyy', { locale: fr })}
              </p>
            </div>
            <div className="space-y-1">
              <span className="text-xs font-medium tracking-wider text-amber-700 uppercase">
                Référence
              </span>
              <p className="flex items-start gap-2 font-mono text-sm font-medium text-stone-800">
                <Hash className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                {/* Un identifiant n'a pas de point de césure naturel : sans
                    `break-all` il déborde de sa colonne. */}
                <span className="min-w-0 break-all">{estimateReference}</span>
              </p>
            </div>
            <div className="space-y-2">
              <span className="text-xs font-medium tracking-wider text-amber-700 uppercase">
                Facture
              </span>
              <group.AppField
                name="wantsInvoice"
                children={(field) => (
                  <field.RadioGroupField
                    options={BOOLEAN_OPTIONS}
                    orientation="horizontal"
                    className="gap-4"
                  />
                )}
              />
            </div>
          </div>

          {/* Insurance option */}
          <div
            className={cn(
              'rounded-xl border-2 border-dashed border-amber-300/60 bg-white/60 p-5',
              'transition-all duration-300 hover:border-amber-400 hover:bg-white'
            )}
          >
            <div className="mb-3 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-linear-to-br from-emerald-400 to-teal-500 shadow-sm">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-stone-800">Assurance voyage</h4>
                {insuranceOption ? (
                  <div className="space-y-0.5">
                    <p className="text-xs text-stone-500">
                      {insuranceOption.nomAssurance} — {insuranceOption.nomFormule}
                    </p>
                    {insuranceOption.extensions.length > 0 && (
                      <p className="text-xs text-stone-400">
                        {insuranceOption.extensions.map((e) => e.label).join(', ')}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-stone-500">
                    {isLongTrip ? '12%' : '7%'} du tarif total
                    {isLongTrip && ' (voyage de plus de 3 mois)'}
                  </p>
                )}
              </div>
            </div>

            <group.AppField
              name="wantsInsurance"
              children={(field) => (
                <field.RadioGroupField
                  options={BOOLEAN_OPTIONS}
                  orientation="horizontal"
                  className="gap-4"
                />
              )}
            />

            {/* Insurance surcharge display */}
            <div className="mt-3 rounded-lg bg-emerald-50/50 px-3 py-2">
              <p className="text-sm text-emerald-800">
                Surcoût assurance :{' '}
                <span className="font-semibold">
                  +
                  {((totalPriceWithInsurance - totalPrice) / 100).toLocaleString('fr-FR', {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                  })}{' '}
                  €
                </span>
              </p>
            </div>

            {insuranceOption?.cgsLink || insuranceOption?.dinLink ? (
              <div className="mt-3 flex gap-4">
                {insuranceOption.cgsLink && (
                  <a
                    href={insuranceOption.cgsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'inline-flex items-center gap-1.5 text-sm font-medium',
                      'text-brand-600 hover:text-brand-700 transition-colors hover:underline'
                    )}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Conditions générales
                  </a>
                )}
                {insuranceOption.dinLink && (
                  <a
                    href={insuranceOption.dinLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'inline-flex items-center gap-1.5 text-sm font-medium',
                      'text-brand-600 hover:text-brand-700 transition-colors hover:underline'
                    )}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Document d&apos;information
                  </a>
                )}
              </div>
            ) : (
              <a
                href="/documents/conditions-assurance.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'mt-3 inline-flex items-center gap-1.5 text-sm font-medium',
                  'text-brand-600 hover:text-brand-700 transition-colors hover:underline'
                )}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Consulter les garanties
              </a>
            )}
          </div>

          {/* Newsletter */}
          <div className="rounded-xl border border-stone-200 bg-white/80 p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium text-stone-700">
              <Newspaper className="h-4 w-4 text-amber-600" />
              Newsletter Massilia Voyages
            </div>
            <group.AppField
              name="newsletterSubscription"
              children={(field) => (
                <field.RadioGroupField options={NEWSLETTER_OPTIONS} orientation="vertical" />
              )}
            />
          </div>

          {/* Terms and conditions */}
          <div
            className={cn(
              'rounded-xl border-2 border-amber-400/40 bg-linear-to-br from-amber-100/50 to-orange-100/30 p-5',
              'transition-all duration-300'
            )}
          >
            <group.AppField
              name="acceptsTermsAndConditions"
              children={(field) => (
                <field.CheckboxField label="J'ai lu et j'accepte les conditions générales de vente" />
              )}
            />
          </div>

          {/* Price Summary */}
          <group.Subscribe
            selector={(s) => s.values.wantsInsurance}
            children={(wantsInsurance) => (
              <div className="rounded-xl border border-stone-200 bg-white/80 p-5">
                <div className="flex justify-between text-sm">
                  <span className="flex items-center gap-1 text-stone-600">
                    {wantsInsurance ? 'Total (avec assurance)' : 'Total'}
                    {wantsInsurance && insuranceOption && (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700">
                        {insuranceOption.nomFormule}
                      </span>
                    )}
                    {wantsInsurance && !insuranceOption && isLongTrip && (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700">
                        12%
                      </span>
                    )}
                  </span>
                  <span className="font-semibold text-stone-900">
                    {((wantsInsurance ? totalPriceWithInsurance : totalPrice) / 100).toLocaleString(
                      'fr-FR',
                      {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      }
                    )}{' '}
                    €
                  </span>
                </div>
              </div>
            )}
          />

          {hasValidationErrors && (
            <div className="pt-4">
              <ImportantNotice title="Il reste quelque chose à corriger" variant="error">
                Une information ne peut pas être envoyée telle quelle. Le champ concerné est signalé
                en rouge plus haut — le plus souvent le nom d&apos;un voyageur, qui ne doit porter
                ni accent ni ponctuation.
              </ImportantNotice>
            </div>
          )}

          {/* Submit button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className={cn(
                'group relative w-full overflow-hidden rounded-xl px-8 py-4',
                'bg-linear-to-r from-amber-500 via-amber-500 to-orange-500',
                'font-semibold text-white shadow-lg shadow-amber-500/25',
                'transition-all duration-300',
                'hover:shadow-xl hover:shadow-amber-500/30',
                'hover:from-amber-600 hover:via-amber-500 hover:to-orange-600',
                'focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 focus:outline-none',
                'disabled:cursor-not-allowed disabled:opacity-60'
              )}
            >
              {/* Shimmer effect */}
              <div className="absolute inset-0 -translate-x-full bg-linear-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-full" />

              <span className="relative flex items-center justify-center gap-2">
                <Sparkles className="h-5 w-5" />
                {isSubmitting ? 'Envoi en cours...' : 'Confirmer ma réservation'}
              </span>
            </button>

            <p className="mt-3 text-center text-xs text-stone-500">
              En confirmant, vous acceptez nos conditions générales de vente
            </p>
          </div>
        </div>
      </SectionWrapper>
    )
  },
})
